import {
    isExternalLink,
    shouldExcludeLink,
    hasUtmParameters,
    appendUtmParams,
    isMessagingLink,
    isShortenerLink,
    checkExcludedClasses,
    matchDomainPattern,
    rewriteMessagingUrl,
} from './modules/LinkUtils.js';

import { parseMessagingUrlForRewrite } from './modules/MessagingParser.js';

import {
    captureClickIds,
    getStoredClickIds,
    appendClickIdsToUrl,
    matchesClickIdAllowlist,
} from './modules/AttributionStore.js';

import {
    generateUtmParams,
    determineUtmCampaign,
} from './modules/UtmGenerator.js';

import {
    resolvePositionContainers,
    findItemPosition,
} from './modules/PositionResolver.js';

import { safeQuerySelector } from './modules/selectorUtils.js';

import {
    createLinkObserver,
    startObserving,
} from './modules/MutationObserverManager.js';

import {
    createDebugOverlay,
    updateDebugList,
    markProcessedLink,
    logDebugInfo,
} from './modules/DebugOverlay.js';

import { attachLeadBeaconListener } from './modules/LeadBeacon.js';

import { mergeSettings } from './modules/defaultSettings.js';

/**
 * Debug-overlay status label for a just-processed link. Exported as a pure
 * function for direct unit testing (pr-review-council finding, v1.6.0):
 * none of the existing UtmLinker pipeline test stubs implement enough of
 * `document` (createElement/appendChild/...) to exercise debugMode through
 * the full `new UtmLinker(...)` pipeline, so the STATUS-STRING decision is
 * isolated here instead of only reachable via that heavier path.
 *
 * A link re-processed under existingUtm='override'/'merge' that ALREADY
 * had utm_* params gets its own `retag-<mode>` marker, distinct from a
 * genuine first-time `added-utm` tag — the debug overlay couldn't tell
 * these apart otherwise, even though they mean very different things for
 * the operator's traffic data.
 *
 * @param {'skip'|'override'|'merge'} existingUtmMode
 * @param {string} originalUrl The link's href BEFORE appendUtmParams() ran.
 * @return {string}
 */
export function debugStatusFor(existingUtmMode, originalUrl) {
    const isRetag = existingUtmMode !== 'skip' && hasUtmParameters(originalUrl);
    return isRetag ? `retag-${existingUtmMode}` : 'added-utm';
}

/**
 * UTM Linker class
 */
export default class UtmLinker {
    constructor(userSettings = {}) {
        // Merge defaults with settings from WP
        this.settings = mergeSettings(userSettings);

        // Host/path
        this.currentHostname = window.location.hostname;
        this.currentPathname = window.location.pathname;

        // Debug - check debugMode and isAdmin to decide whether to show the debug panel
        this.debugMode = Boolean(this.settings.debugMode) && Boolean(this.settings.isAdmin);
        this.debugElements = null;

        // Storage
        this.processedLinks = [];
        this.maxProcessedLinks = 100;

        // #65: WeakSet of <a> nodes already tagged by processLinks(). On a
        // MutationObserver-triggered rescan, skip any node already in this set
        // to avoid re-running the full pipeline (regex tests, URL parsing,
        // generateUtmParams, appendUtmParams) on links that haven't changed.
        // WeakSet (not Set) so garbage-collected <a> nodes don't leak memory.
        // Reset is unnecessary — a node that was tagged stays tagged (its href
        // was mutated in place); a node that was SKIPPED (internal, excluded,
        // shortener, ...) is also safe to skip again because the conditions
        // are stable for a given DOM state.
        //
        // #78: The WeakSet is NEVER cleared during the page lifetime — it
        // persists skip decisions across MutationObserver rescans. This is
        // safe because the skip conditions (isExternalLink, shouldExcludeLink,
        // isShortenerLink, ...) depend on settings (excludeDomains,
        // shortenerSkip, excludeClasses, ...) that are read from this.settings,
        // which is fixed at construction time. In the WP plugin context,
        // settings only change via the admin save flow, which always reloads
        // the page — so a settings change produces a fresh UtmLinker instance
        // with a fresh WeakSet, and already-processed links are re-evaluated
        // against the new settings on the next pass. If this engine is ever
        // used in a dynamic context where settings change WITHOUT a page
        // reload (e.g. a SPA that mutates settings at runtime), the WeakSet
        // would need to be cleared on settings change, or skip decisions
        // would go stale and newly-excluded/shortener domains would not be
        // honored for already-processed links.
        this.processedLinkNodes = new WeakSet();

        // utm_term position counter — LEGACY mode only (positionContainerSelector
        // unset/empty/invalid/zero-matched on the current page — see
        // modules/PositionResolver.js). Instance-level (not a local re-seeded
        // on every processLinks() call) so a MutationObserver-triggered
        // rescan keeps incrementing from where the previous scan left off
        // instead of restarting at 1 — otherwise a newly-inserted link on a
        // rescan could get a utm_term that collides with one already
        // assigned earlier.
        //
        // When container mode IS active (v1.10.0), utm_term instead comes
        // from findItemPosition() — a deterministic function of the link's
        // DOM position relative to the configured list-item containers, with
        // no counter state to manage (this.position is simply never touched
        // in that mode). See processLinks() below.
        this.position = 1;

        // Current pass's list-item containers (v1.10.0), or null for legacy
        // per-link counter mode. Re-resolved at the top of EVERY
        // processLinks() call (never cached across passes) so a
        // MutationObserver rescan sees containers that grew since the last
        // pass.
        this.positionContainers = null;

        // Opt-in (default OFF): capture inbound ad click-ids into a
        // first-party cookie and expose them for outbound passthrough.
        // Gated on the master toggle, a non-empty passthrough allowlist, AND
        // the resolved consent signal — see modules/AttributionStore.js. An
        // empty allowlist means nothing could ever be passed through, so
        // there is nothing worth attributing to; skip the cookie write
        // entirely rather than persist a tracking cookie no config can use.
        this.storedClickIds = {};
        const clickIdPassthroughConfigured = this.settings.clickIdPassthrough
            && Array.isArray(this.settings.clickIdPassthroughDomains)
            && this.settings.clickIdPassthroughDomains.length > 0;

        if (clickIdPassthroughConfigured) {
            captureClickIds(this.settings.consentGranted, this.debugMode);
            this.storedClickIds = getStoredClickIds(this.settings.consentGranted);
        }

        // Opt-in (default OFF, v1.7.0): fire a pay-per-lead metering beacon
        // to go-grad's ingest endpoint on a click/tel-tap against an
        // admin-configured partner attribution map. Gated on
        // settings.leadBeacon — OFF attaches NO listener at all, so this
        // stays byte-identical to a build without the feature. The listener
        // is bound ONCE here (constructor time), not inside processLinks()
        // — unlike href-tagging, it must survive MutationObserver rescans
        // without being re-bound. See modules/LeadBeacon.js.
        if (this.settings.leadBeacon) {
            attachLeadBeaconListener(this.settings);
        }

        // Init
        this.init();
    }

    init() {
        this.processLinks();
        this.observeDynamicLinks();

        if (this.debugMode) {
            this.initDebugMode();
        }
    }

    initDebugMode() {
        this.debugElements = createDebugOverlay({
            currentHostname: this.currentHostname,
            currentPathname: this.currentPathname,
            // Static configured value now (v1.9.0 medium-toggle refactor —
            // no more path/DOM-derived classification), or 'off' when the
            // mediumEnabled toggle is off.
            utmMedium: this.settings.mediumEnabled ? this.settings.mediumValue : 'off',
            utmCampaign: determineUtmCampaign(this.currentPathname),
            i18n: this.settings.i18n,
        });

        logDebugInfo({
            excludeDomains: this.settings.excludeDomains,
        });
        this.updateDebugList();
    }

    processLinks() {
        // Array.from() creates a static snapshot — getElementsByTagName('a')
        // returns a LIVE HTMLCollection that can mutate mid-iteration if
        // processLinks() modifies the DOM (adding attributes, wrapping
        // elements), causing skipped or double-processed links (#50).
        const links = Array.from(document.getElementsByTagName('a'));

        // Re-resolved every pass (v1.10.0) — never cached across
        // MutationObserver rescans, since dynamically-inserted content can
        // add new list-item containers between passes. null = legacy
        // per-link counter mode (see this.position's own doc comment).
        this.positionContainers = resolvePositionContainers(this.settings.positionContainerSelector);

        // Link-processing budget (bug-hunt-council HIGH #1): cap the number
        // of links processed in a single pass to prevent main-thread
        // blocking on pages with thousands of links. Default 5000 (well
        // above any realistic page); overridable via settings.maxLinks.
        // The cap counts EVERY iterated link (including skipped ones — a
        // page with 10000 internal links and 100 external still iterates
        // 10100 DOM nodes), so this is a per-pass iteration guard, not a
        // "tagged links" guard. A debug-mode console.warn fires when the
        // cap is reached so the operator can raise maxLinks or split the
        // page if they genuinely have that many links.
        const maxLinks = typeof this.settings.maxLinks === 'number' && this.settings.maxLinks > 0
            ? this.settings.maxLinks
            : 5000;
        let processedCount = 0;
        let capReached = false;

        for (const link of links) {
            if (processedCount >= maxLinks) {
                capReached = true;
                break;
            }
            processedCount++;

            // #65: skip <a> nodes already processed in a previous pass. On a
            // MutationObserver-triggered rescan (cookie banner, ad script,
            // infinite scroll, ...), the live HTMLCollection returned by
            // getElementsByTagName('a') includes EVERY anchor on the page,
            // not just the newly-added ones. Without this guard, every rescan
            // re-runs the full pipeline on all previously-tagged links.
            // The cap counter above still increments (processedCount++) so
            // the budget guard reflects the real iteration cost.
            if (this.processedLinkNodes.has(link)) {
                continue;
            }

            // A single malformed link (a throwing DOM read, an admin-configured
            // selector that turns out invalid, ...) must never abort tagging
            // for the rest of the page — isolate each link's processing.
            try {
                if (!link.href || link.href === '#' || link.href.startsWith('javascript:')) {
                    continue;
                }

                if (!isExternalLink(link, this.currentHostname)) {
                    continue;
                }

                // Opt-out (default ON, F1/T3): a link that is ALREADY a known
                // short-URL is skipped entirely — its own utm_* would be noise
                // (the destination's UTM, set before shortening, wins after
                // the redirect), and some shorteners choke on extra params.
                // Runs before the messenger-rewrite gate below: a shortened
                // messenger share-link (rare, but possible) is skipped the
                // same way as any other shortener, not rewritten.
                if (this.settings.shortenerSkip && isShortenerLink(link, this.settings.shortenerDomains)) {
                    continue;
                }

                // Opt-in (default OFF): rewrite the embedded destination inside a
                // messenger deep-link instead of excluding it outright. When this
                // fails (no rewritable embedded URL, already tagged, excluded
                // domain/class/attribute), fall through unchanged into the
                // standard pipeline below, which excludes messaging links
                // unconditionally via shouldExcludeLink() — identical to the
                // messengerRewrite=false behavior.
                if (this.settings.messengerRewrite && isMessagingLink(link)) {
                    // Container mode (v1.10.0): the rank is a deterministic
                    // function of DOM position, not a counter — read it
                    // without consuming anything. Legacy mode: peek the
                    // counter's CURRENT value (unchanged from before
                    // v1.10.0) and only advance it below on success.
                    const messengerPosition = this.positionContainers
                        ? findItemPosition(link, this.positionContainers)
                        : this.position;

                    if (this.tryRewriteMessengerLink(link, messengerPosition)) {
                        if (!this.positionContainers) {
                            this.position++;
                        }
                        continue;
                    }
                }

                if (shouldExcludeLink(
                    link,
                    this.settings.excludeClasses,
                    this.settings.excludeDomains,
                    this.settings.excludeProtocols || [],
                    this.debugMode,
                    this.settings.existingUtm
                )) {
                    continue;
                }

                const originalUrl = link.href;

                // F2 (T4): an already-tagged link under existingUtm='skip' is
                // excluded above, at the shouldExcludeLink() call (its own
                // `existingUtmMode === 'skip' && hasUtmParameters(...)` gate,
                // reached with the SAME link.href) — so an equivalent check
                // reached here would be unconditionally unreachable dead
                // code (pr-review-council finding, v1.6.0: removed). Any
                // link that DOES reach this point either has NO existing
                // UTM, or existingUtm is 'override'/'merge' — both cases
                // fall through into generateUtmParams()+appendUtmParams()
                // below, which do the actual per-key override/merge (see
                // that call's existingUtmMode argument).
                // Same container-mode-vs-legacy-counter split as the
                // messenger-rewrite branch above: container mode looks up a
                // deterministic DOM-position rank (may be null — an intro
                // link before the first container, see
                // generateUtmParams()'s own null-position contract); legacy
                // mode consumes the instance counter exactly as before
                // v1.10.0.
                const position = this.positionContainers
                    ? findItemPosition(link, this.positionContainers)
                    : this.position++;

                const utmParams = generateUtmParams(
                    link,
                    position,
                    this.currentHostname,
                    this.currentPathname,
                    this.settings.mediumEnabled,
                    this.settings.mediumValue,
                    this.settings.utmContentRules,
                    this.settings.normalizeValues
                );

                const newUrl = appendUtmParams(originalUrl, utmParams, this.settings.i18n, this.settings.existingUtm);
                link.href = newUrl;

                // Opt-in (default OFF): append stored inbound click-ids on top of
                // the UTM tagging above, but only to links whose host matches the
                // clickIdPassthroughDomains allowlist. Runs in addition to UTM
                // tagging, never in place of it.
                if (this.settings.clickIdPassthrough) {
                    this.applyClickIdPassthrough(link);
                }

                if (this.debugMode) {
                    markProcessedLink(link, debugStatusFor(this.settings.existingUtm, originalUrl), utmParams);
                }

                this.addToProcessedLinks({ element: link, originalUrl, newUrl, utmParams });
            } catch (e) {
                if (this.debugMode) {
                    console.error('UTM Linker: skipping a link after an unexpected error', e);
                }
                continue;
            } finally {
                // #65: mark this node as processed so a MutationObserver rescan
                // skips it. Covers ALL exit paths: tagged links (href mutated),
                // skipped links (internal/excluded/shortener/messenger), and
                // errored links (caught above). A node that threw will likely
                // throw again on rescan, so skipping it is correct.
                this.processedLinkNodes.add(link);
            }
        }

        if (capReached && this.debugMode) {
            console.warn(`UTM Linker: link cap reached (${maxLinks} links in a single pass) — remaining links on this page were not processed. Raise maxLinks in settings if this is expected.`);
        }

        if (this.debugMode) {
            this.updateDebugList();
        }
    }

    /**
     * Try to rewrite a messenger deep-link's embedded destination URL with
     * UTM params (messengerRewrite opt-in path). Returns false — leaving the
     * link untouched — whenever the standard exclusion rules would also
     * apply (data-no-utm, exclude-class, excluded domain), when there is no
     * rewritable embedded URL, or when the embedded URL already has utm_*.
     *
     * @param {HTMLAnchorElement} link The messenger link element
     * @param {number|null} position Current link position, for utm_term —
     *   see generateUtmParams()'s own null-position contract (v1.10.0).
     * @return {boolean} True if the link was rewritten in place
     */
    tryRewriteMessengerLink(link, position) {
        // Hard-exclusion parity with the standard path (LinkUtils.js
        // shouldExcludeLink() + appendUtmParams()): whatsapp://send must
        // never be rewritten. The standard path never reaches those checks
        // for a messenger link when messengerRewrite=true — this function
        // runs FIRST and `continue`s on success — so the guard has to live
        // here too, or messengerRewrite silently diverges from the
        // messengerRewrite=false behavior for this one link shape.
        if (link.href && link.href.toLowerCase().includes('whatsapp://send')) {
            return false;
        }

        if (link.hasAttribute?.('data-no-utm')) {
            return false;
        }

        if (checkExcludedClasses(link, this.settings.excludeClasses, this.debugMode)) {
            return false;
        }

        const href = link.href;
        const parsed = parseMessagingUrlForRewrite(href);

        if (!parsed?.embeddedUrl || parsed.hasUtm) {
            return false;
        }

        let embeddedHost;
        try {
            embeddedHost = new URL(parsed.embeddedUrl).hostname;
        } catch (e) {
            return false;
        }

        const excludeDomains = this.settings.excludeDomains || [];
        if (excludeDomains.some((domain) => matchDomainPattern(embeddedHost, domain))) {
            return false;
        }

        const utmParams = generateUtmParams(
            link,
            position,
            this.currentHostname,
            this.currentPathname,
            this.settings.mediumEnabled,
            this.settings.mediumValue,
            this.settings.utmContentRules,
            this.settings.normalizeValues
        );

        const newUrl = rewriteMessagingUrl(href, utmParams);
        if (!newUrl || newUrl === href) {
            return false;
        }

        link.href = newUrl;

        if (this.debugMode) {
            markProcessedLink(link, 'messenger-rewrite', utmParams);
        }

        this.addToProcessedLinks({ element: link, originalUrl: href, newUrl, utmParams });

        return true;
    }

    /**
     * Opt-in (default OFF): append stored inbound click-ids to an outbound
     * link whose host matches the clickIdPassthroughDomains allowlist. An
     * empty allowlist is inert — nothing is ever appended. Never touches
     * messenger links (those `continue` earlier in processLinks(), before
     * reaching here) and never duplicates a click-id key already on the URL.
     *
     * @param {HTMLAnchorElement} link The link element, already UTM-tagged
     */
    applyClickIdPassthrough(link) {
        if (Object.keys(this.storedClickIds).length === 0) return;
        if (!matchesClickIdAllowlist(link.hostname, this.settings.clickIdPassthroughDomains)) return;

        const taggedUrl = appendClickIdsToUrl(link.href, this.storedClickIds);
        if (taggedUrl !== link.href) {
            link.href = taggedUrl;
        }
    }

    addToProcessedLinks(data) {
        if (this.processedLinks.length >= this.maxProcessedLinks) {
            this.processedLinks.shift();
        }
        this.processedLinks.push(data);
    }

    observeDynamicLinks() {
        const observer = createLinkObserver(() => this.processLinks(), {
            throttleTime: 250,
        });

        // #65: narrow the observer scope to a configured content container
        // instead of document.body. On a content site, document.body gets
        // mutated by everything (cookie banners, ad scripts, infinite scroll,
        // lazy-loaded images, third-party widgets) — each mutation fires the
        // observer callback. A narrowed scope ignores mutations outside the
        // content area. Default empty = document.body (current behavior).
        // Falls back to document.body when the selector matches nothing or is
        // invalid (never breaks link tagging).
        // safeQuerySelector wraps the try-catch and falls back to document.body
        // on invalid/empty/zero-matched selectors. Admin-side validation IS
        // available via validateObserverScopeSelector (src/admin/utils/
        // positionSelectorValidation.js) — the same generic CSS selector syntax
        // check used for positionContainerSelector. A future admin UI field
        // under Scope → DOM Detection can surface this; until then an invalid
        // observerScopeSelector is a config error the admin will notice when
        // no links get tagged.
        const target = safeQuerySelector(this.settings.observerScopeSelector, document.body);

        startObserving(observer, target);
    }

    updateDebugList() {
        if (this.debugElements?.debugList) {
            updateDebugList(this.debugElements.debugList, this.processedLinks, this.settings.i18n);
        }
    }
}
